import { DeleteOutlined, ExportOutlined, PlusOutlined, ReloadOutlined, UploadOutlined } from "@ant-design/icons";
import { Button, Checkbox, Modal, Space, Spin, Tooltip, Typography, message } from "antd";
import { useCallback, useEffect, useRef, useState } from "react";
import { downloadBlob, serverFetch, serverFetchBinary } from "../../core/server";
import "./database-blocks.css";
import { getReportFormTipName } from "./reports/report-form-tip";
import SprReportForm from "./reports/spr-report-form";

const getId = (row) => row.idForm ?? row.IdForm;

const isOnline = (row) => {
  const value = Number(row?.formTip ?? row?.FormTip);
  return !Number.isFinite(value) || value === 0 || value >= 100;
};

const readTextFile = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result ?? ""));
    reader.onerror = () => reject(new Error("Не удалось прочитать файл"));
    reader.readAsText(file, "UTF-8");
  });

const DatabaseReports = () => {
  const fileInputRef = useRef(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editorOpen, setEditorOpen] = useState(false);
  const [selected, setSelected] = useState([]);

  const loadItems = useCallback(async () => {
    setLoading(true);
    try {
      const result = await serverFetch("app/database/reports");
      setItems((Array.isArray(result) ? result : []).filter(isOnline));
    } catch (e) {
      setItems([]);
      message.error(e.userMessage || e.message || "Не удалось загрузить отчеты");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadItems();
  }, [loadItems]);

  const toggleSelected = (id, checked) => {
    setSelected((current) => (checked ? [...current, id] : current.filter((item) => item !== id)));
  };

  const exportReport = async (id, name) => {
    try {
      const response = await serverFetchBinary(`app/database/reports/${id}/export`, {
        queryParams: { format: "xmlk" }
      });
      await downloadBlob(response, `${name || "report"}.xmlk`);
    } catch (e) {
      message.error(e.userMessage || e.message || "Не удалось выгрузить отчет");
    }
  };

  const importReports = async (files) => {
    for (const file of files) {
      const content = await readTextFile(file);
      await serverFetch("app/database/reports/import", {
        method: "POST",
        bodyData: { fileName: file.name, content }
      });
    }
    message.success("Отчеты импортированы");
    await loadItems();
  };

  const copySelected = async () => {
    if (!selected.length) return;
    await serverFetch("app/database/reports/copy", { method: "POST", bodyData: { idForms: selected } });
    message.success("Отчеты скопированы");
    setSelected([]);
    await loadItems();
  };

  const deleteReport = (id) => {
    Modal.confirm({
      title: "Удалить отчет?",
      okText: "Удалить",
      okButtonProps: { danger: true },
      cancelText: "Отмена",
      onOk: async () => {
        await serverFetch("app/database/reports", { method: "DELETE", bodyData: { idForm: id } });
        await loadItems();
      }
    });
  };

  return (
    <div>
      <Space className="db-toolbar" size={8} wrap>
        <Button type="primary" size="small" icon={<PlusOutlined />} onClick={() => { setEditingId(null); setEditorOpen(true); }}>
          Добавить
        </Button>
        <Button size="small" icon={<UploadOutlined />} onClick={() => fileInputRef.current?.click()}>
          Импорт
        </Button>
        <Button size="small" disabled={!selected.length} onClick={copySelected}>
          Копировать
        </Button>
        <Button size="small" icon={<ReloadOutlined />} onClick={loadItems} loading={loading} />
      </Space>
      <input
        ref={fileInputRef}
        type="file"
        accept=".xmlk,.xml"
        multiple
        hidden
        onChange={(event) => {
          const files = Array.from(event.target.files || []);
          event.target.value = "";
          if (files.length) importReports(files).catch((e) => message.error(e.message));
        }}
      />
      <Spin spinning={loading}>
        {items.length === 0 && !loading ? (
          <Typography.Paragraph type="secondary" className="db-empty">
            Отчеты не найдены
          </Typography.Paragraph>
        ) : (
          <div className="db-blocks">
            <div className="db-block db-block--head">
              <span className="db-block__check-spacer" />
              <div className="db-report-cols">
                <span>Название</span>
                <span>Тип</span>
                <span>Порядок</span>
                <span title="Уровень доступа">Уровень</span>
              </div>
              <span className="db-block__actions" />
            </div>
            {items.map((row) => {
              const id = getId(row);
              const name = row.formName ?? row.FormName ?? "—";
              const tip = getReportFormTipName(row.formTip ?? row.FormTip);
              const idx = row.idx ?? row.Idx;
              const level = row.userLevel ?? row.UserLevel;
              return (
                <div key={String(id)} className="db-block" onClick={() => { setEditingId(id); setEditorOpen(true); }}>
                  <Checkbox
                    checked={selected.includes(id)}
                    onClick={(event) => event.stopPropagation()}
                    onChange={(event) => toggleSelected(id, event.target.checked)}
                  />
                  <div className="db-report-cols">
                    <span className="db-block__title-text" title={name}>{name}</span>
                    <span>{tip || ""}</span>
                    <span>{idx ?? ""}</span>
                    <span>{level ?? ""}</span>
                  </div>
                  <div className="db-block__actions" onClick={(event) => event.stopPropagation()}>
                    <Tooltip title="Экспорт">
                      <Button type="text" size="small" icon={<ExportOutlined />} onClick={() => exportReport(id, name)} />
                    </Tooltip>
                    <Tooltip title="Удалить">
                      <Button type="text" size="small" danger icon={<DeleteOutlined />} onClick={() => deleteReport(id)} />
                    </Tooltip>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </Spin>
      <SprReportForm
        open={editorOpen}
        idForm={editingId}
        canEdit
        onClose={() => setEditorOpen(false)}
        onSaved={loadItems}
      />
    </div>
  );
};

export default DatabaseReports;
